import {
  EVENT_TYPE_CATEGORY_MAP,
  type EventOption,
  type EventType,
  type EventTypeCategory,
  type InteractionEvent,
} from "../types";

/**
 * The activity state machine.
 *
 * An entity's `eventOptions` array is not just a list of buttons — it's a
 * little workflow. Each option declares the steps that may follow it (`next`),
 * whether it can open the activity (`start`), and what has to have been logged
 * before it makes sense (`requires`). Reading the flow is then a lookup rather
 * than a guess: you're standing on a step, and the step says where you can go.
 *
 * Why this rather than inferring from categories: real places branch. Leaving
 * the line at an attraction can mean the main line, the lockers, or the preshow
 * — all three at once, and the person logging is the one who knows which. And
 * some steps are gated: you can't end a preshow you never started, and
 * VelociCoaster won't let you ride until you've been to the lockers. A category
 * ordering can express neither, so it used to guess, and the watch tile and the
 * web app guessed slightly differently.
 *
 * Two things stay out of the graph on purpose:
 *
 *  - **Delays are interrupts, not steps.** A ride breaking down doesn't move you
 *    along the queue, so DELAY_START is offered alongside whatever comes next
 *    and DELAY_END takes over entirely while a delay is open. Wiring them into
 *    every node would double the size of every flow to say the same thing.
 *  - **Nothing is ever forbidden.** The flow decides what gets a one-tap button;
 *    the full list is always behind "More…". This is a travel journal, and the
 *    day does not always go in order.
 *
 * Options with no flow fields at all fall back to [guessNextOptions], the
 * category-ordering heuristic this replaced — entities customised before flows
 * existed keep working exactly as they did.
 */

/** Imported options may omit `category`; every option has an event type. */
export function categoryOf(opt: EventOption): EventTypeCategory {
  return opt.category ?? EVENT_TYPE_CATEGORY_MAP[opt.eventType];
}

/** Delays sit outside the flow — they interrupt it rather than advance it. */
export function isDelayOption(opt: EventOption): boolean {
  const c = categoryOf(opt);
  return c === "DELAY_START" || c === "DELAY_END";
}

/** True once any option carries flow wiring, i.e. this entity has a workflow. */
export function hasActivityFlow(options: EventOption[]): boolean {
  return options.some((o) => o.start === true || o.next !== undefined);
}

export interface ActivityFlowState {
  /** The step the activity is standing on, when one could be identified. */
  current: EventOption | null;
  /** The steps that may be taken now, most likely first. */
  next: EventOption[];
  /** The graph offers a real choice here, not just one obvious next step. */
  branching: boolean;
  /** A delay is open: `next` is how you close it, and nothing else applies. */
  delayOpen: boolean;
  /** The activity reached a step with nothing after it. */
  complete: boolean;
  /** Whether the flow graph answered, or the old heuristic had to. */
  source: "flow" | "guess";
}

/**
 * Work out which option a logged event was.
 *
 * The label written with the event is the strongest signal and the web app
 * always writes one; a label matching no step is somebody's ad-hoc note
 * ("Bathroom break"), which shouldn't move the activity anywhere, so it
 * resolves to nothing. Falling back to the event type covers the watch, which
 * only writes a label when an entity offers the same event type twice.
 *
 * A dining round's mirrored event ("Round 2 Placed") is the exception: its
 * label names the round, not a step, but it is an Order Placed or Order
 * Received all the same, so it goes by its event type. Read by label, every
 * round would count as a note and the meal would never get past Seated.
 */
export function matchLoggedOption(
  options: EventOption[],
  event: { eventType: EventType; customLabel?: string | null; roundIndex?: number | null },
): EventOption | undefined {
  if (event.customLabel && event.roundIndex == null) {
    return options.find((o) => o.displayName === event.customLabel);
  }
  return options.find((o) => o.eventType === event.eventType);
}

/** The steps that can open an activity: marked ones, else the START group. */
export function entrySteps(options: EventOption[]): EventOption[] {
  const marked = options.filter((o) => o.start === true);
  if (marked.length > 0) return marked;
  return options.filter((o) => categoryOf(o) === "START");
}

/**
 * Events in the order they happened. Timestamps can be edited after the fact,
 * so array order isn't reliable — but the array is all there is when events
 * arrive without resolvable timestamps (as they do in tests and from partially
 * synced writes).
 */
function inTimeOrder(events: InteractionEvent[]): InteractionEvent[] {
  const millis = (e: InteractionEvent): number | null => {
    const ms = e.eventTimestamp?.toMillis?.();
    return typeof ms === "number" ? ms : null;
  };
  if (events.some((e) => millis(e) === null)) return events;
  return [...events].sort((a, b) => millis(a)! - millis(b)!);
}

/** The last logged event that actually moved the activity along. */
function currentStep(options: EventOption[], ordered: InteractionEvent[]): EventOption | null {
  for (let i = ordered.length - 1; i >= 0; i--) {
    const opt = matchLoggedOption(options, ordered[i]!);
    if (opt && !isDelayOption(opt)) return opt;
  }
  return null;
}

/** Read the activity's position in its entity's flow. */
export function activityFlowState(
  options: EventOption[],
  events: InteractionEvent[],
): ActivityFlowState {
  const ordered = inTimeOrder(events);
  const current = currentStep(options, ordered);
  const delayOpen =
    events.filter((ev) => ev.eventType === "DELAY_START").length >
    events.filter((ev) => ev.eventType === "DELAY_END").length;

  const guessed = (): ActivityFlowState => ({
    current,
    next: guessNextOptions(options, events),
    branching: false,
    delayOpen,
    complete: !delayOpen && ordered.length > 0 && guessNextOptions(options, events).length === 0,
    source: "guess",
  });

  if (!hasActivityFlow(options)) return guessed();

  if (delayOpen) {
    return {
      current,
      next: options.filter((o) => categoryOf(o) === "DELAY_END"),
      branching: false,
      delayOpen: true,
      complete: false,
      source: "flow",
    };
  }

  const loggedIds = new Set(
    ordered.map((ev) => matchLoggedOption(options, ev)?.id).filter((id): id is string => !!id),
  );
  // A step is on offer once what it needs is logged — and, for a step that
  // only happens once, until it has been.
  const satisfied = (opt: EventOption) =>
    (opt.requires ?? []).every((id) => loggedIds.has(id)) && !(opt.once && loggedIds.has(opt.id));

  if (!current) {
    return {
      ...settled(entrySteps(options).filter(satisfied)),
      current: null,
      delayOpen: false,
      source: "flow",
    };
  }

  // A step nobody wired up — a custom option added to an entity that otherwise
  // has a flow. The graph has nothing to say about it, so guess from here.
  if (current.next === undefined) return guessed();

  const byId = new Map(options.map((o) => [o.id, o] as const));
  const onward = current.next
    .map((id) => byId.get(id))
    .filter((o): o is EventOption => !!o)
    .filter(satisfied);

  return {
    ...settled(onward),
    current,
    delayOpen: false,
    source: "flow",
  };
}

/**
 * Finish a set of onward steps: a running activity can always be flagged as
 * delayed, but a finished one can't.
 */
function settled(onward: EventOption[]): Pick<ActivityFlowState, "next" | "branching" | "complete"> {
  return { next: onward, branching: onward.length > 1, complete: onward.length === 0 };
}

/** Append the delay-start steps an activity can always reach while running. */
export function withDelayOption(
  state: ActivityFlowState,
  options: EventOption[],
): EventOption[] {
  if (state.delayOpen || state.complete || !state.current) return state.next;
  const already = new Set(state.next.map((o) => o.id));
  const delays = options.filter((o) => categoryOf(o) === "DELAY_START" && !already.has(o.id));
  return [...state.next, ...delays];
}

// ---------------------------------------------------------------------------
// Flow authoring helpers
// ---------------------------------------------------------------------------

/** One step's wiring, keyed by option id — see `config/defaultEventOptions`. */
export interface FlowSpec {
  start?: true;
  next?: string[];
  requires?: string[];
  once?: true;
}

/**
 * Attach a flow to a list of options. Kept separate from the option literals so
 * a flow reads as a graph rather than as a field buried in each button, and so
 * a typo in an id fails loudly here instead of silently offering nothing.
 */
export function withFlow(
  options: EventOption[],
  spec: Record<string, FlowSpec>,
): EventOption[] {
  const ids = new Set(options.map((o) => o.id));
  for (const [id, wiring] of Object.entries(spec)) {
    if (!ids.has(id)) throw new Error(`activity flow: no event option "${id}"`);
    for (const target of [...(wiring.next ?? []), ...(wiring.requires ?? [])]) {
      if (!ids.has(target)) throw new Error(`activity flow: "${id}" points at missing "${target}"`);
    }
  }
  return options.map((opt) => {
    const wiring = spec[opt.id];
    if (!wiring) return opt;
    return {
      ...opt,
      ...(wiring.start ? { start: true } : {}),
      ...(wiring.next ? { next: wiring.next } : {}),
      ...(wiring.requires ? { requires: wiring.requires } : {}),
      ...(wiring.once ? { once: true } : {}),
    };
  });
}

/**
 * Give every option a fresh id, rewriting the flow to match. Copying a default
 * flow onto an entity regenerates ids so two entities never share one; without
 * the rewrite every `next` would dangle and the entity would arrive flow-less.
 */
export function remapOptionIds(
  options: EventOption[],
  nextId: () => string,
): EventOption[] {
  const mapping = new Map(options.map((o) => [o.id, nextId()] as const));
  const remap = (ids: string[] | undefined) =>
    ids?.map((id) => mapping.get(id) ?? id).filter((id) => id !== undefined);
  return options.map((o) => ({
    ...o,
    id: mapping.get(o.id)!,
    ...(o.next ? { next: remap(o.next)! } : {}),
    ...(o.requires ? { requires: remap(o.requires)! } : {}),
  }));
}

/**
 * Old step names and the built-in step each one now is, from the October 2026
 * type merge: shows and excursions became Experiences, lounges and
 * restaurants became Food & drink.
 */
const STEP_NAME_ALIASES: Readonly<Record<string, string>> = {
  "done dining": "done",
  "show started": "started",
  "show start": "started",
  "show ended": "ended",
  "show end": "ended",
  "finished": "ended",
  "check in": "checked in",
};

/**
 * How two step lists recognise the same step: by name, ignoring case and
 * spacing, with the old names from the type merge mapped to their new ones.
 */
export function stepNameKey(name: string): string {
  const k = name.trim().toLowerCase();
  return STEP_NAME_ALIASES[k] ?? k;
}

/**
 * Give a flow-less list of options the flow its entity type would have, matched
 * step by step on name.
 *
 * Nearly every entity in the database carries its own copy of the steps —
 * the migration wrote one onto each — so without this, flows would be a thing
 * only brand-new entities ever had. Matching on the displayed name is exactly
 * how a person reads these lists ("this is the Entered Line step"), and the ids
 * can't be matched on because they were regenerated per entity.
 *
 * Edges pointing at steps this entity doesn't offer are dropped. If that would
 * leave a step with nowhere to go, it's left unwired instead, so it falls back
 * to the guess rather than looking like the end of the activity.
 *
 * Steps a merged type used to call something else (a show's "Show Started",
 * a restaurant's "Done Dining") still match the step that replaced them.
 */
export function adoptFlow(options: EventOption[], defaults: EventOption[]): EventOption[] {
  if (options.length === 0 || hasActivityFlow(options)) return options;

  const key = stepNameKey;
  const ownIdByName = new Map<string, string>();
  for (const o of options) {
    if (!ownIdByName.has(key(o.displayName))) ownIdByName.set(key(o.displayName), o.id);
  }
  const defaultByName = new Map(defaults.map((d) => [key(d.displayName), d] as const));
  if (![...ownIdByName.keys()].some((name) => defaultByName.has(name))) return options;

  const translate = (ids: string[] | undefined): string[] | undefined =>
    ids
      ?.map((id) => defaults.find((d) => d.id === id)?.displayName)
      .map((name) => (name ? ownIdByName.get(key(name)) : undefined))
      .filter((id): id is string => !!id);

  return options.map((opt) => {
    const source = defaultByName.get(key(opt.displayName));
    if (!source) return opt;
    const next = translate(source.next);
    // An emptied-out `next` means the entity dropped every step that followed
    // this one — that's a gap in the mapping, not the end of the activity.
    const keepNext = next !== undefined && (next.length > 0 || source.next?.length === 0);
    const requires = translate(source.requires);
    return {
      ...opt,
      ...(source.start ? { start: true } : {}),
      ...(keepNext ? { next } : {}),
      ...(requires && requires.length > 0 ? { requires } : {}),
      ...(source.once ? { once: true } : {}),
    };
  });
}

/**
 * Drop a step and take its edges with it.
 *
 * Deleting "At Lockers" from a ride that hasn't got any is how the absence is
 * said — but a step left pointing at it is gated on something that no longer
 * exists, and the step behind it goes quiet. The runtime is forgiving about
 * dangling ids; the stored flow shouldn't rely on that.
 */
export function removeOptionFromFlow(options: EventOption[], id: string): EventOption[] {
  return options
    .filter((o) => o.id !== id)
    .map((o) => ({
      ...o,
      ...(o.next ? { next: o.next.filter((n) => n !== id) } : {}),
      ...(o.requires ? { requires: o.requires.filter((r) => r !== id) } : {}),
    }));
}

/**
 * Strip an entity's own wiring so it goes back to borrowing its type's.
 * The reverse of materialising the borrowed flow to edit it — and the same
 * thing, from the runtime's side, as never having wired it at all.
 */
export function withoutFlow(options: EventOption[]): EventOption[] {
  return options.map((o) => {
    const bare = { ...o };
    delete bare.start;
    delete bare.next;
    delete bare.requires;
    delete bare.once;
    return bare;
  });
}

export interface FlowIssue {
  /** The step the problem is about, or null when it's about the flow itself. */
  optionId: string | null;
  severity: "error" | "warning";
  message: string;
}

/**
 * What's wrong with a hand-edited flow. Editing entity steps is how a flow gets
 * broken — delete "Preshow Start" and "Preshow End" is gated on a step that no
 * longer exists — so the entity form runs this and shows what it finds.
 */
export function validateActivityFlow(options: EventOption[]): FlowIssue[] {
  if (!hasActivityFlow(options)) return [];
  const issues: FlowIssue[] = [];
  const byId = new Map(options.map((o) => [o.id, o] as const));
  const flowSteps = options.filter((o) => !isDelayOption(o));

  for (const opt of options) {
    for (const id of opt.next ?? []) {
      if (!byId.has(id)) {
        issues.push({ optionId: opt.id, severity: "error", message: `leads to a step that no longer exists` });
        break;
      }
    }
    for (const id of opt.requires ?? []) {
      if (!byId.has(id)) {
        issues.push({ optionId: opt.id, severity: "error", message: `needs a step that no longer exists` });
        break;
      }
      if (id === opt.id) {
        issues.push({ optionId: opt.id, severity: "error", message: `needs itself` });
        break;
      }
    }
  }

  const entries = entrySteps(options);
  if (entries.length === 0) {
    issues.push({
      optionId: null,
      severity: "error",
      message: "No step can start this activity — mark at least one.",
    });
  }

  // Walk the graph so unreachable steps and dead ends show up as what they are:
  // buttons that will only ever be found under "More…".
  const reached = new Set<string>();
  const queue = entries.map((o) => o.id);
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reached.has(id)) continue;
    reached.add(id);
    for (const nextId of byId.get(id)?.next ?? []) {
      if (!reached.has(nextId)) queue.push(nextId);
    }
  }

  for (const opt of flowSteps) {
    if (!reached.has(opt.id)) {
      issues.push({ optionId: opt.id, severity: "warning", message: "can't be reached from any starting step" });
    } else if (opt.next !== undefined && opt.next.length === 0 && categoryOf(opt) !== "END") {
      issues.push({ optionId: opt.id, severity: "warning", message: "ends the activity, but isn't an ending step" });
    } else if (opt.next === undefined) {
      issues.push({ optionId: opt.id, severity: "warning", message: "has no next step set — what follows it is guessed" });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// The pre-flow heuristic, kept for entities that have no flow
// ---------------------------------------------------------------------------

/** Event types that only make sense once their counterpart has been logged. */
const PAIRED_PREREQ: Partial<Record<EventType, EventType>> = {
  PRESHOW_END: "PRESHOW_START",
  ORDER_RECEIVED: "ORDER_PLACED",
  PICKED_UP: "ORDER_PLACED",
};

/**
 * Best-effort ordering by category, for entities whose options carry no flow:
 *   nothing logged  → the START options (or the lead-up, for entities with no
 *                     explicit start step)
 *   a delay is open → the DELAY_END option(s)
 *   in progress     → ACTIVE (if not yet), then END / DELAY_START, then milestones
 *   ended           → nothing
 */
export function guessNextOptions(
  options: EventOption[],
  events: InteractionEvent[],
): EventOption[] {
  const loggedTypes = new Set(events.map((ev) => ev.eventType));
  const loggedCats = events.map((ev) => EVENT_TYPE_CATEGORY_MAP[ev.eventType]);
  const has = (c: EventTypeCategory) => loggedCats.includes(c);
  const delayOpen =
    events.filter((ev) => ev.eventType === "DELAY_START").length >
    events.filter((ev) => ev.eventType === "DELAY_END").length;
  const byCat = (c: EventTypeCategory) => options.filter((o) => categoryOf(o) === c);

  if (delayOpen) return byCat("DELAY_END");
  if (has("END")) return [];

  const hasActiveOption = byCat("ACTIVE").length > 0;
  // "Has the activity actually got going?" — used to gate the finishing step.
  const underway = loggedCats.some((c) => c === "ACTIVE" || c === "MILESTONE");
  const relevant = (opt: EventOption): boolean => {
    const prereq = PAIRED_PREREQ[opt.eventType];
    if (prereq && !loggedTypes.has(prereq)) return false;
    if (categoryOf(opt) === "END") {
      // Don't suggest finishing until it's started (or there is no "active"
      // step at all, e.g. a restaurant), and not before any progress.
      if (hasActiveOption && !has("ACTIVE")) return false;
      if (!underway) return false;
    }
    return true;
  };
  const keep = (opts: EventOption[]) => opts.filter(relevant);

  if (events.length === 0) {
    const starters = byCat("START");
    if (starters.length) return starters;
    // No explicit "start" step: offer the lead-up options in document order,
    // through the first "active"/"end" option.
    const firstMainIdx = options.findIndex((o) => {
      const c = categoryOf(o);
      return c === "ACTIVE" || c === "END";
    });
    const leadUp = firstMainIdx >= 0 ? options.slice(0, firstMainIdx + 1) : options;
    return keep(leadUp);
  }

  // In progress — surface the likely next steps first, milestones last so
  // they're the ones that get truncated when there are a lot of options.
  const tiers: EventOption[][] = [];
  if (!has("ACTIVE")) tiers.push(byCat("ACTIVE"));
  tiers.push(keep(byCat("END")));
  tiers.push(byCat("DELAY_START"));
  tiers.push(keep(byCat("MILESTONE")));

  const seen = new Set<string>();
  const out: EventOption[] = [];
  for (const tier of tiers) {
    for (const opt of tier) {
      const key = String(opt.id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(opt);
    }
  }
  return out;
}
